import { SearchIcon } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';

import type { SkuDTO } from '@invenco/products-interface';

import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/core/combobox';
import { InputGroupAddon } from '@/components/core/input-group';
import { useGateways } from '@/gateways/GatewayProvider';
import { SkuBadge } from '@/pages/seller/components/sku-badge';
import { useAutoCompleteQuery } from '@/shared/hooks/queries';

type AddAsnLineSkuComboboxProps = {
  onSelect: (sku: SkuDTO) => void;
  onCancel: () => void;
};

type SkuOption = {
  value: string;
  label: string;
  description: string | null;
  sku: SkuDTO;
};

export function AddAsnLineSkuCombobox({ onSelect, onCancel }: AddAsnLineSkuComboboxProps) {
  const { productsGateway } = useGateways();
  const [isOpen, setIsOpen] = useState(false);
  const ignoreNextCloseRef = useRef(false);
  const {
    options: matchingSkus,
    onSearch,
    isLoading,
  } = useAutoCompleteQuery({
    parentKey: 'asn-line-skus',
    queryParams: { archived: false },
    query: (input, { signal }) => productsGateway.getSkus(input, { signal }),
    enabled: true,
  });

  const options = useMemo<SkuOption[]>(
    () =>
      matchingSkus.map((sku) => ({
        value: sku.id,
        label: sku.name,
        description: sku.description,
        sku,
      })),
    [matchingSkus],
  );

  return (
    <Combobox
      open={isOpen}
      onOpenChange={(nextOpen) => {
        setIsOpen(nextOpen);

        if (!nextOpen) {
          queueMicrotask(() => {
            if (ignoreNextCloseRef.current) {
              ignoreNextCloseRef.current = false;
              return;
            }

            onCancel();
          });
        }
      }}
      filteredItems={options}
      items={options}
      onInputValueChange={(value) => onSearch(typeof value === 'string' ? value : '')}
      onValueChange={(value) => {
        const selectedValue = typeof value === 'string' ? value : undefined;
        if (!selectedValue) return;
        const selectedOption = options.find((option) => option.value === selectedValue);
        if (!selectedOption) return;
        ignoreNextCloseRef.current = true;
        onSelect(selectedOption.sku);
      }}
    >
      <ComboboxInput
        autoFocus
        className="h-full w-full rounded-none border-0 px-2 shadow-none has-[[data-slot=input-group-control]:focus-visible]:border-0 has-[[data-slot=input-group-control]:focus-visible]:ring-0"
        placeholder="Search by SKU or description"
        aria-label="SKU"
        showClear
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            if (isOpen) {
              ignoreNextCloseRef.current = true;
              setIsOpen(false);
              return;
            }

            onCancel();
          }
        }}
      >
        <InputGroupAddon align="inline-start">
          <SearchIcon className="size-3.5" />
        </InputGroupAddon>
      </ComboboxInput>
      <ComboboxContent>
        <ComboboxEmpty>{isLoading ? 'Loading SKUs...' : 'No matching SKUs'}</ComboboxEmpty>
        <ComboboxList>
          {(option: SkuOption) => (
            <ComboboxItem key={option.value} value={option.value}>
              <div className="flex min-w-0 items-center gap-2">
                <SkuBadge name={option.label} type={option.sku.type} />
                {option.description ? (
                  <span className="truncate text-sm">{option.description}</span>
                ) : null}
              </div>
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}
